import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  DailyEntryRecord,
  DEFAULT_TRACKER_SETTINGS,
  EMPTY_TRACKER_SUMMARY,
  TrackerId,
  TRACKER_LABELS,
  TrackerSettingsRecord,
  TrackerSummaryRecord,
} from '../types/dailyBite';
import { BadgeContext, evaluateBadges } from './dailyBiteBadges';
import { computeAndSaveCorrelations } from './correlationService';

const LOCK_WINDOW_MS = 72 * 60 * 60 * 1000;

export function todayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map((part) => Number(part));
  return new Date(y, (m || 1) - 1, d || 1);
}

export function isEntryLocked(entry: DailyEntryRecord | null): boolean {
  if (!entry) return false;
  if (entry.lockedAt) return true;
  const entryDate = parseDateKey(entry.date);
  const endOfDayEntry = new Date(entryDate);
  endOfDayEntry.setHours(23, 59, 59, 999);
  return Date.now() - endOfDayEntry.getTime() > LOCK_WINDOW_MS;
}

function entryRef(uid: string, dateKey: string) {
  return doc(db, 'users', uid, 'daily_entries', dateKey);
}

function settingsRef(uid: string) {
  return doc(db, 'users', uid, 'tracker_settings', 'settings');
}

function summaryRef(uid: string) {
  return doc(db, 'users', uid, 'tracker_summary', 'current');
}

function monthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

function buildDateKeyRange(end: Date, days: number): string[] {
  const keys: string[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    keys.push(todayKey(addDays(end, -i)));
  }
  return keys;
}

export async function getTrackerSettings(uid: string): Promise<TrackerSettingsRecord> {
  const snap = await getDoc(settingsRef(uid));
  if (!snap.exists()) {
    return { ...DEFAULT_TRACKER_SETTINGS };
  }
  const data = snap.data() as Partial<TrackerSettingsRecord>;
  return {
    ...DEFAULT_TRACKER_SETTINGS,
    ...data,
    optedIn: { ...DEFAULT_TRACKER_SETTINGS.optedIn, ...(data.optedIn || {}) },
    nicotineForms: data.nicotineForms || [],
    sharing: {
      clinician: {
        ...DEFAULT_TRACKER_SETTINGS.sharing.clinician,
        ...(data.sharing?.clinician || {}),
      },
      partner: {
        ...DEFAULT_TRACKER_SETTINGS.sharing.partner,
        ...(data.sharing?.partner || {}),
      },
    },
    reminders: { ...DEFAULT_TRACKER_SETTINGS.reminders, ...(data.reminders || {}) },
    streakFreezes: { ...DEFAULT_TRACKER_SETTINGS.streakFreezes, ...(data.streakFreezes || {}) },
    customHabits: data.customHabits || [],
    customPills: {
      water: data.customPills?.water || [],
      protein: data.customPills?.protein || [],
      caffeine: data.customPills?.caffeine || [],
    },
    correlations: {
      ...DEFAULT_TRACKER_SETTINGS.correlations!,
      ...(data.correlations || {}),
    },
  };
}

export async function saveTrackerSettings(
  uid: string,
  settings: Partial<TrackerSettingsRecord>,
): Promise<void> {
  await setDoc(
    settingsRef(uid),
    {
      ...settings,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function getDailyEntry(uid: string, dateKey: string): Promise<DailyEntryRecord | null> {
  const snap = await getDoc(entryRef(uid, dateKey));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, any>;
  return {
    id: snap.id,
    date: data.date || dateKey,
    water: data.water,
    protein: data.protein,
    caffeine: data.caffeine,
    alcohol: data.alcohol,
    nicotine: data.nicotine,
    mood: data.mood,
    gratitude: data.gratitude,
    digestion: data.digestion,
    customHabits: data.customHabits,
    completeness: typeof data.completeness === 'number' ? data.completeness : 0,
    lockedAt: data.lockedAt ?? null,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

const TRACKER_IDS: TrackerId[] = [
  'water',
  'protein',
  'caffeine',
  'alcohol',
  'nicotine',
  'mood',
  'gratitude',
  'digestion',
];

/**
 * Whether a single tracker counts as logged for the day.
 *
 * Exported because the dashboard renders a per-tracker ✓/– strip and must not
 * re-derive these rules: several are not what a caller would guess (alcohol is
 * logged at 0 units — a dry day is a logged state; mood needs `loggedAt`, not
 * just a value; digestion needs a non-empty `movements` array). A second copy
 * would drift from `computeCompleteness`, which reads the same predicate.
 */
export function isTrackerLogged(entry: Partial<DailyEntryRecord>, trackerId: TrackerId): boolean {
  switch (trackerId) {
    case 'water':
      return Boolean(entry.water && entry.water.totalMl > 0);
    case 'protein':
      return Boolean(entry.protein && entry.protein.totalGrams > 0);
    case 'caffeine':
      return Boolean(entry.caffeine && entry.caffeine.totalMg > 0);
    case 'alcohol':
      // Alcohol counts as "logged" once the member has engaged with the
      // slider at all — marking a dry day (0 units) is a logged state.
      return Boolean(entry.alcohol);
    case 'nicotine':
      return Boolean(entry.nicotine && entry.nicotine.entries && entry.nicotine.entries.length > 0);
    case 'mood':
      return Boolean(entry.mood && entry.mood.loggedAt);
    case 'gratitude':
      return Boolean(entry.gratitude && entry.gratitude.response && entry.gratitude.response.trim().length > 0);
    case 'digestion':
      return Boolean(entry.digestion && entry.digestion.movements && entry.digestion.movements.length > 0);
    default:
      return false;
  }
}

export function computeCompleteness(
  entry: Partial<DailyEntryRecord>,
  settings: TrackerSettingsRecord,
): number {
  const optedIn = TRACKER_IDS.filter((id) => settings.optedIn[id]);
  if (optedIn.length === 0) return 0;
  const logged = optedIn.filter((id) => isTrackerLogged(entry, id));
  return logged.length / optedIn.length;
}

export function countUnloggedOptedInTrackers(
  entry: Partial<DailyEntryRecord> | null,
  settings: TrackerSettingsRecord,
): number {
  const base: Partial<DailyEntryRecord> = entry || {};
  return TRACKER_IDS.filter(
    (id) => settings.optedIn[id] && !isTrackerLogged(base, id),
  ).length;
}

export interface TrackerStripItem {
  id: TrackerId;
  label: string;
  logged: boolean;
}

/**
 * The opted-in trackers with their logged state, in the canonical tracker
 * order — the shape the dashboard's "Today" strip renders directly.
 *
 * Trackers the member has opted out of are omitted rather than shown unlogged:
 * the strip is a picture of what they chose to track, not a list of what they
 * are failing to do.
 */
export function buildTodayTrackerStrip(
  entry: Partial<DailyEntryRecord> | null,
  settings: TrackerSettingsRecord,
): TrackerStripItem[] {
  const base: Partial<DailyEntryRecord> = entry || {};
  return TRACKER_IDS.filter((id) => settings.optedIn[id]).map((id) => ({
    id,
    label: TRACKER_LABELS[id],
    logged: isTrackerLogged(base, id),
  }));
}

function stripUndefined(value: any): any {
  if (value === null) return null;
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    if (
      typeof value.toDate === 'function' ||
      typeof value.toMillis === 'function' ||
      value instanceof Date
    ) {
      return value;
    }
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = stripUndefined(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  return value;
}

export async function upsertDailyEntryField(
  uid: string,
  dateKey: string,
  partial: Partial<DailyEntryRecord>,
  settings: TrackerSettingsRecord,
): Promise<DailyEntryRecord> {
  const existing = await getDailyEntry(uid, dateKey);
  if (existing && isEntryLocked(existing)) {
    throw new Error('This day is locked — message your care team to request an edit.');
  }
  const merged: Partial<DailyEntryRecord> = {
    ...(existing || {}),
    ...partial,
    date: dateKey,
  };
  const completeness = computeCompleteness(merged, settings);
  // Only write the fields in `partial` plus metadata. Firestore's merge:true
  // keeps everything else; this avoids sending stale `undefined` values from a
  // previously-loaded DailyEntryRecord back to setDoc.
  const payload: Record<string, any> = {
    ...partial,
    date: dateKey,
    completeness,
    updatedAt: serverTimestamp(),
  };
  if (!existing) {
    payload.createdAt = serverTimestamp();
  }
  const cleaned = stripUndefined(payload);
  await setDoc(entryRef(uid, dateKey), cleaned, { merge: true });
  return {
    id: dateKey,
    ...merged,
    completeness,
  } as DailyEntryRecord;
}

export function timestampNow(): Timestamp {
  return Timestamp.now();
}

export async function listRecentDailyEntries(
  uid: string,
  days: number = 60,
): Promise<DailyEntryRecord[]> {
  const snap = await getDocs(
    query(
      collection(db, 'users', uid, 'daily_entries'),
      orderBy('date', 'desc'),
      limit(Math.max(1, days)),
    ),
  );
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, any>;
    return {
      id: d.id,
      date: data.date || d.id,
      water: data.water,
      protein: data.protein,
      caffeine: data.caffeine,
      alcohol: data.alcohol,
      nicotine: data.nicotine,
      mood: data.mood,
      gratitude: data.gratitude,
      digestion: data.digestion,
      customHabits: data.customHabits,
      completeness: typeof data.completeness === 'number' ? data.completeness : 0,
      lockedAt: data.lockedAt ?? null,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as DailyEntryRecord;
  });
}

export async function getTrackerSummary(uid: string): Promise<TrackerSummaryRecord> {
  const snap = await getDoc(summaryRef(uid));
  if (!snap.exists()) return { ...EMPTY_TRACKER_SUMMARY };
  const data = snap.data() as Partial<TrackerSummaryRecord>;
  return {
    ...EMPTY_TRACKER_SUMMARY,
    ...data,
    freezeDaysAppliedThisMonth: data.freezeDaysAppliedThisMonth || [],
    earnedBadges: data.earnedBadges || [],
    upcomingBadges: data.upcomingBadges || [],
  };
}

/**
 * Walk backwards from today, counting consecutive logged days. Gaps consume
 * a freeze from this month's budget if one is available and the gap day is
 * in the current or previous calendar month. Previous-month gaps only get a
 * freeze if that month already has one recorded in
 * `freezeDaysAppliedThisMonth` — we never retroactively freeze past months.
 */
function computeCurrentStreak(
  loggedDates: Set<string>,
  todayDateKey: string,
  currentMonth: string,
  freezesAlreadyUsed: number,
  existingFreezeDays: Set<string>,
): {
  current: number;
  newFreezesApplied: string[];
} {
  const MAX_LOOKBACK = 400;
  const budget = Math.max(0, 2 - freezesAlreadyUsed);
  let remaining = budget;
  const newFreezes: string[] = [];
  let streak = 0;

  let cursor = parseDateKey(todayDateKey);
  if (!loggedDates.has(todayDateKey)) {
    cursor = addDays(cursor, -1);
  }

  for (let step = 0; step < MAX_LOOKBACK; step += 1) {
    const key = todayKey(cursor);
    const month = key.slice(0, 7);
    const isLogged = loggedDates.has(key);
    const alreadyFrozen = existingFreezeDays.has(key);

    if (isLogged) {
      streak += 1;
    } else if (alreadyFrozen) {
      streak += 1;
    } else if (month === currentMonth && remaining > 0) {
      remaining -= 1;
      newFreezes.push(key);
      streak += 1;
    } else {
      break;
    }
    cursor = addDays(cursor, -1);
  }

  return { current: streak, newFreezesApplied: newFreezes };
}

/**
 * Longest streak across the loaded history. Does not use freezes — this is
 * the honest record of back-to-back days the member actually logged.
 */
function computeLongestStreak(loggedDates: Set<string>, rangeKeys: string[]): number {
  let longest = 0;
  let current = 0;
  for (const k of rangeKeys) {
    if (loggedDates.has(k)) {
      current += 1;
      if (current > longest) longest = current;
    } else {
      current = 0;
    }
  }
  return longest;
}

/**
 * Recompute streaks + badges from the last 60 days of entries and write the
 * result to `users/{uid}/tracker_summary/current`. Called after every entry
 * save so the UI stays consistent with what the member just logged.
 */
export async function recomputeAndSaveSummary(
  uid: string,
  settings: TrackerSettingsRecord,
): Promise<TrackerSummaryRecord> {
  const now = new Date();
  const todayDateKey = todayKey(now);
  const currentMonth = monthKey(now);

  const [entries, existingSummary] = await Promise.all([
    listRecentDailyEntries(uid, 60),
    getTrackerSummary(uid),
  ]);

  const entriesByDate = new Map<string, DailyEntryRecord>();
  const loggedDates = new Set<string>();
  for (const e of entries) {
    entriesByDate.set(e.date, e);
    if ((e.completeness || 0) > 0) loggedDates.add(e.date);
  }

  const freezesWereForThisMonth = existingSummary.month === currentMonth;
  const priorFreezesUsed = freezesWereForThisMonth
    ? existingSummary.freezesUsedThisMonth
    : 0;
  const priorFreezeDays = freezesWereForThisMonth
    ? new Set(existingSummary.freezeDaysAppliedThisMonth)
    : new Set<string>();

  const { current, newFreezesApplied } = computeCurrentStreak(
    loggedDates,
    todayDateKey,
    currentMonth,
    priorFreezesUsed,
    priorFreezeDays,
  );

  const allFreezeDays = new Set([...priorFreezeDays, ...newFreezesApplied]);
  const freezesUsedThisMonth = priorFreezesUsed + newFreezesApplied.length;
  const freezesAvailable = Math.max(0, 2 - freezesUsedThisMonth);

  const rangeKeys = buildDateKeyRange(now, entries.length || 1);
  const historicalLongest = computeLongestStreak(loggedDates, rangeKeys);
  const longestStreak = Math.max(existingSummary.longestStreak, historicalLongest, current);

  const last30Keys = buildDateKeyRange(now, 30);
  const last14Keys = buildDateKeyRange(now, 14);

  const badgeContext: BadgeContext = {
    entries,
    entriesByDate,
    loggedDates,
    settings,
    today: now,
    last30Keys,
    last14Keys,
  };

  const nowTs = Timestamp.fromDate(now);
  const { earned, upcoming } = evaluateBadges(badgeContext, existingSummary.earnedBadges, nowTs);

  const next: TrackerSummaryRecord = {
    currentStreak: current,
    longestStreak,
    freezesAvailable,
    freezesUsedThisMonth,
    freezeDaysAppliedThisMonth: Array.from(allFreezeDays).sort(),
    month: currentMonth,
    earnedBadges: earned,
    upcomingBadges: upcoming,
    lastComputedForDate: todayDateKey,
    lastComputedAt: nowTs,
  };

  await setDoc(summaryRef(uid), next, { merge: true });

  // Phase 3 — fire-and-forget correlations recompute so the /intelligence
  // cards stay in sync with the latest entries. We deliberately skip
  // passing wearable observations here: loading them on every daily save
  // is wasteful, and the service preserves any previously-computed
  // wearable-backed card so it doesn't regress to "waiting on sync". The
  // /intelligence page is where the wearable-backed recompute happens.
  //
  // We pass the 60-day entries slice we already loaded so the service
  // doesn't have to re-query. Errors are swallowed — a failed correlation
  // compute must never block the save path.
  computeAndSaveCorrelations({
    uid,
    entries,
    settings,
    summary: next,
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('correlations recompute failed', err);
  });

  return next;
}

/** Format a Firestore Timestamp as "HH:MM" in the member's local time. */
export function formatTimeOfDay(ts?: Timestamp | null): string {
  if (!ts || typeof ts.toDate !== 'function') return '';
  const d = ts.toDate();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Build a Timestamp from a `dateKey` (YYYY-MM-DD) + "HH:MM" string. Used when
 * the member corrects the auto-stamped "last at" on a time-sensitive tracker.
 */
export function timestampFromDateAndTime(dateKey: string, time: string): Timestamp {
  const [y, mo, d] = dateKey.split('-').map((n) => Number(n));
  const [h, mi] = time.split(':').map((n) => Number(n));
  const jsDate = new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0, 0, 0);
  return Timestamp.fromDate(jsDate);
}

export const DAILY_BITE_TRACKER_IDS = TRACKER_IDS;
